import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, MapPin, BedDouble, Star } from "lucide-react";
import { BRAND, inr } from "./constants";
import { BrandMark } from "./BrandingPanel";

const HERO = "https://static.prod-images.emergentagent.com/jobs/20196b41-9c71-4ff5-a96b-aa9402135555/images/d81f2a66535137d006e9fb43605e62c273f111654e198bb853e43cef6845772a.jpeg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POINTS = ["Owner sees money, targets & every bed live", "Managers run beds, bookings, payments & inventory", "Guests request beds & raise issues from one link", "9 PM WhatsApp summary to owner + manager"];

export function StayCommandHero() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 mt-6" data-testid="staycommand-hero">
      <div className="relative overflow-hidden rounded-3xl min-h-[420px] flex items-end md:items-center" style={{ background: BRAND.green }}>
        <img src={HERO} alt="Property owner and manager tracking beds on phone and tablet" className="absolute inset-0 w-full h-full object-cover object-right opacity-90" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(0,48,37,.94) 0%, rgba(0,48,37,.78) 42%, rgba(0,48,37,.05) 75%)" }} />
        <div className="relative p-6 md:p-10 max-w-xl text-white hs-rise">
          <span data-testid="staycommand-new-badge" className="bed-blink inline-flex items-center rounded-full px-3 py-1 text-[11px] font-bold tracking-widest" style={{ background: BRAND.orange }}>NEW</span>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-bold leading-tight">StayCommand</h2>
          <p className="mt-1 text-base md:text-lg text-white/90">Owner controlled. Manager driven. Every bed, rupee and request tracked live.</p>
          <ul className="mt-4 space-y-1.5 text-sm text-white/90">
            {POINTS.map((p) => <li key={p} className="flex gap-2"><span style={{ color: BRAND.orange }}>●</span>{p}</li>)}
          </ul>
          <div className="mt-6 flex flex-wrap gap-2">
            <Link to="/homestay/owner" data-testid="staycommand-start-btn" className="rounded-full px-6 py-3 font-bold text-sm inline-flex items-center gap-2 shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: BRAND.orange }}>
              Start 7-day free trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login" data-testid="staycommand-manager-login" className="rounded-full px-6 py-3 font-bold text-sm bg-white/10 border border-white/40 backdrop-blur">Manager login</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const BANNER = "https://customer-assets-wrfwihn1.emergentagent.net/job_assist-on-demand/artifacts/a5e3fde201ed77ed_IMG-20261008-WA0025.jpg";

export function EconomicalBanner() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 pt-6" data-testid="economical-banner">
      <div className="relative rounded-3xl overflow-hidden border shadow-xl" style={{ borderColor: BRAND.line }}>
        <img src={BANNER} alt="Economical Hostel, Dormitory and Home Stay — free SaaS owner and manager login, ₹999 monthly" className="w-full block" />
        <Link to="/homestay/owner" data-testid="banner-get-started-link" aria-label="Add your property — get started free"
          className="absolute rounded-full transition-[box-shadow] hover:shadow-[0_0_0_4px_rgba(244,81,24,.35)]"
          style={{ left: "31%", width: "38%", top: "91.5%", height: "7%" }} />
        <Link to="/homestay/owner" data-testid="banner-owner-login-link" aria-label="Owner login" className="absolute" style={{ left: "1%", width: "26%", top: "37%", height: "9%" }} />
        <Link to="/login" data-testid="banner-manager-login-link" aria-label="Manager login" className="absolute" style={{ left: "74%", width: "25%", top: "37%", height: "9%" }} />
      </div>
    </section>
  );
}

const PRICING = [
  ["₹37", "per day", "billed monthly"],
  ["₹999", "per month", "Featured — all tools unlocked"],
  ["FREE", "SaaS login", "your property brand name"],
  ["7 days", "free trial", "starts on approval"],
];

export function PricingStrip() {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 mt-6" data-testid="pricing-strip">
      <div className="rounded-3xl p-4 md:p-5 grid grid-cols-2 md:grid-cols-5 gap-3 items-center" style={{ background: "#FFF7ED", border: `2px solid ${BRAND.orange}33` }}>
        {PRICING.map(([big, unit, sub], i) => (
          <div key={big} data-testid={`pricing-item-${i}`} className="rounded-2xl bg-white p-3 text-center hs-rise" style={{ animationDelay: `${i * 80}ms` }}>
            <div className="font-display text-2xl md:text-3xl font-bold" style={{ color: i === 2 ? "#DC2626" : i === 1 ? BRAND.green : BRAND.orange }}>{big}</div>
            <div className="text-xs font-bold" style={{ color: BRAND.green }}>{unit}</div>
            <div className="text-[11px]" style={{ color: BRAND.muted }}>{sub}</div>
          </div>
        ))}
        <Link to="/homestay/owner" data-testid="pricing-get-started" className="col-span-2 md:col-span-1 rounded-full py-3 text-center font-bold text-white text-sm shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: BRAND.orange }}>
          Add your property — free →
        </Link>
      </div>
    </section>
  );
}

export function PublicStays({ type = "" }) {
  const [all, setAll] = useState([]);
  useEffect(() => { axios.get(`${API}/homestay/public`).then((r) => setAll(r.data.items)).catch(() => {}); }, []);
  const items = type ? all.filter((p) => p.property_type === type) : all;
  if (!all.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8" data-testid="public-stays">
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: BRAND.orange }}>Live beds near you</div>
      <h3 className="font-display text-2xl font-bold" style={{ color: BRAND.green }}>Stays open right now</h3>
      {items.length === 0 && <div className="mt-3 text-sm" style={{ color: BRAND.muted }} data-testid="public-stays-empty">No {type} stays live yet — check back soon.</div>}
      <div className="mt-3 flex gap-3 overflow-x-auto no-scrollbar pb-2">
        {items.map((p) => (
          <Link key={p.id} to={`/homestay/p/${p.id}`} data-testid={`public-stay-${p.id}`} className="shrink-0 w-64 rounded-2xl border-2 bg-white p-4 transition-transform hover:-translate-y-1" style={{ borderColor: BRAND.line }}>
            <div className="flex items-center gap-2">
              <BrandMark property={p} size={34} />
              <span className="chip text-[10px]">{p.property_type.toUpperCase()}</span>
            </div>
            <div className="mt-2 font-bold truncate" style={{ color: BRAND.green }}>{p.brand_name || p.name}</div>
            {p.tagline && <div className="text-[11px] truncate" style={{ color: BRAND.muted }}>{p.tagline}</div>}
            {p.rating_count > 0 && <div className="text-xs font-bold inline-flex items-center gap-1" data-testid={`public-stay-rating-${p.id}`}><Star className="w-3.5 h-3.5" style={{ color: "#F59E0B", fill: "#F59E0B" }} /> {p.rating_avg} <span className="font-normal" style={{ color: BRAND.muted }}>({p.rating_count})</span></div>}
            <div className="text-xs inline-flex items-center gap-1" style={{ color: BRAND.muted }}><MapPin className="w-3 h-3" /> {p.locality}, {p.city}</div>
            <div className="mt-2 text-sm flex justify-between"><span className="inline-flex items-center gap-1"><BedDouble className="w-4 h-4" style={{ color: "#16A34A" }} /> {p.vacant}/{p.total} free</span>{p.monthly_from > 0 && <b>{inr(p.monthly_from)}/mo</b>}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}
